import { useEffect, useState } from "react";
import { Link, useLoaderData, useRevalidator, useSearchParams } from "react-router-dom";
import { RichText } from "../components/RichText";
import { SizingFields } from "../components/SizingFields";
import { StatusField } from "../components/StatusField";
import { Icon } from "../components/Icon";
import { Avatar } from "../components/Avatar";
import { OwnerPicker } from "../components/OwnerPicker";
import { Panel, PanelHead } from "../components/Panel";
import { PriorityBadge } from "../components/Priority";
import { CommentsPanel } from "../components/CommentComposer";
import { DetailAside, DetailField } from "../components/DetailField";
import { ListPanel } from "../components/ListPanel";
import { Select, TextArea, TextInput } from "../components/Field";
import { TagPicker } from "../components/TagPicker";
import { RequestMilestones } from "../components/RequestMilestones";
import { MoveAccountField } from "../components/MoveAccountField";
import { OrderInput } from "../components/OrderInput";
import { OutcomePanel, RequestHeading } from "../components/RequestFields";
import { addComment, updateRequest } from "../data/api";
import { formatDateTime } from "../data/format";
import { initialsOf } from "../../../src/shared/names";
import { useRoster } from "../data/RosterContext";
import { useTags } from "../data/TagsContext";
import { FINAL_OWNER_LABEL, FINAL_OWNER_ORDER, NEXT_TONE_CLASS, SLA_CLASS, SLA_LABEL, SOURCE_LABEL, STAFF_SOURCES, TYPE_OPTIONS } from "../data/status";
import type { Comment, CommentVisibility, RequestSource, RequestType, TimelineEntry, Owner } from "../data/types";
import type { RequestData } from "../data/loaders";

export function RequestDetail() {
  // A 404 from the loader is thrown as a Response and handled by the route's
  // errorElement, so here the request always exists.
  const { request, milestones: accountMilestones, accounts } = useLoaderData() as RequestData;
  // Opened from an account's own table? Then back belongs to that account, not
  // to every customer's requests. The destination comes from the request's own
  // accountId, never from the parameter, so the link cannot be pointed
  // anywhere by editing the URL.
  const cameFromAccount = useSearchParams()[0].get("from") === "account";
  const { members } = useRoster();
  // Someone who has left is offered to nobody, but a request they still hold
  // (closed, or not yet reassigned) must keep showing their name.
  const withCurrent = (current: Owner | null): Owner[] => (current && !members.some((m) => m.id === current.id) ? [...members, current] : members);
  const { tags } = useTags();
  const revalidator = useRevalidator();
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [visibility, setVisibility] = useState<CommentVisibility>("internal");

  // The content edit draft, seeded from the request when edit mode opens.
  const [title, setTitle] = useState(request.title);
  const [priority, setPriority] = useState<number>(request.priority);
  const [description, setDescription] = useState(request.description);
  const [problem, setProblem] = useState(request.problem);
  // The source link is saved on blur or Enter, not per keystroke.
  const [sourceLink, setSourceLink] = useState(request.sourceLabel === "—" ? "" : request.sourceLabel);
  useEffect(() => setSourceLink(request.sourceLabel === "—" ? "" : request.sourceLabel), [request.sourceLabel]);
  const saveSourceLink = () => {
    const current = request.sourceLabel === "—" ? "" : request.sourceLabel;
    if (sourceLink.trim() !== current) void patch({ sourceLabel: sourceLink.trim() });
  };

  // Apply an edit, then revalidate so the loader refetches the request AND its
  // timeline — the change and the activity entry it produced appear together.
  const patch = async (p: Parameters<typeof updateRequest>[1]) => {
    setSaving(true);
    try {
      await updateRequest(request.id, p);
      revalidator.revalidate();
    } finally {
      setSaving(false);
    }
  };

  function startEdit() {
    setTitle(request.title);
    setPriority(request.priority);
    setDescription(request.description);
    setProblem(request.problem);
    setEditing(true);
  }

  async function saveEdit() {
    if (title.trim() === "") return;
    await patch({ title: title.trim(), priority, description, problem: problem.trim() });
    setEditing(false);
  }

  return (
    <div className="mx-auto max-w-[1000px] px-6 py-8">
      <Link
        to={cameFromAccount ? `/accounts/${request.accountId}` : "/requests"}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-[var(--text-2)] transition-colors hover:text-[var(--text)] focus-visible:focus-ring"
      >
        <Icon name="arrow-left" />
        {cameFromAccount ? request.account : "All requests"}
      </Link>

      {request.archivedAt && (
        <div role="status" className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--border)] bg-gray-100 px-4 py-3 text-sm">
          <span>
            <span className="font-medium">Archived</span>
            <span className="text-[var(--text-2)]"> · out of every list and count, and never shown to the customer. Its status is kept for when it comes back.</span>
          </span>
          <button
            type="button"
            onClick={() => void patch({ archived: false })}
            disabled={saving}
            className="rounded-md border border-[var(--border-strong)] bg-[var(--bg)] px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus-visible:focus-ring disabled:opacity-50"
          >
            Unarchive
          </button>
        </div>
      )}

      <div className="mb-6 flex items-start gap-3">
        {editing ? (
          <div className="w-[72px] flex-none">
            <Select aria-label="Edit priority" value={priority} onChange={(e) => setPriority(Number(e.target.value))}>
              {[0, 1, 2, 3, 4].map((p) => (
                <option key={p} value={p}>
                  P{p}
                </option>
              ))}
            </Select>
          </div>
        ) : (
          <PriorityBadge priority={request.priority} />
        )}
        <RequestHeading request={request} editing={editing} title={title} onTitleChange={setTitle} />
        {editing ? (
          <div className="flex flex-none gap-2">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="rounded-md border border-[var(--border-strong)] bg-[var(--bg)] px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus-visible:focus-ring"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void saveEdit()}
              disabled={saving || title.trim() === ""}
              className="rounded-md bg-brand-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700 focus-visible:focus-ring disabled:opacity-50"
            >
              Save
            </button>
          </div>
        ) : (
          <div className="flex flex-none gap-2">
            {!request.archivedAt && (
              <button
                type="button"
                onClick={() => void patch({ archived: true })}
                disabled={saving}
                className="rounded-md border border-[var(--border-strong)] bg-[var(--bg)] px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus-visible:focus-ring disabled:opacity-50"
              >
                Archive
              </button>
            )}
            <button
              type="button"
              onClick={startEdit}
              className="flex flex-none items-center gap-1.5 rounded-md border border-[var(--border-strong)] bg-[var(--bg)] px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 focus-visible:focus-ring"
            >
              <Icon name="edit" />
              Edit
            </button>
          </div>
        )}
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_280px]">
        <div className="grid gap-5">
          {/* Customer-visible, like the description. */}
          <OutcomePanel id="outcome-h" value={request.problem} editing={editing} draft={problem} onDraftChange={setProblem} />

          <Panel labelledBy="desc-h">
            <PanelHead id="desc-h" title="Description" />
            {editing ? (
              <div className="p-4">
                <TextArea aria-label="Edit description" value={description} onChange={(e) => setDescription(e.target.value)} />
              </div>
            ) : (
              <RichText text={request.description} />
            )}
          </Panel>

          <CommentsPanel
            id="comments-h"
            comments={request.comments}
            renderComment={(c) => <CommentRow key={c.id} comment={c} />}
            onPost={async (body) => {
              await addComment(request.id, { body, visibility });
              revalidator.revalidate();
            }}
            controls={
              <label className="flex items-center gap-2 text-sm text-[var(--text-2)]">
                <input
                  type="checkbox"
                  checked={visibility === "customer"}
                  onChange={(e) => setVisibility(e.target.checked ? "customer" : "internal")}
                  className="h-4 w-4 accent-[var(--brand-600)]"
                />
                Customer can see
              </label>
            }
          />

          <ListPanel id="activity-h" title="Activity" items={request.timeline} ordered renderItem={(entry) => <TimelineRow key={entry.id} entry={entry} />} />
        </div>

        <DetailAside>
          <StatusField value={request.status} disabled={saving} onChange={(status, customerNote) => patch(customerNote ? { status, customerNote } : { status })} />
          <DetailField label="Type">
            <Select
              aria-label="Type"
              value={request.type}
              disabled={saving}
              onChange={(e) => void patch({ type: e.target.value as RequestType })}
            >
              {TYPE_OPTIONS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Select>
          </DetailField>
          <MoveAccountField
            accounts={accounts}
            accountId={request.accountId}
            accountName={request.account}
            milestoneCount={request.milestones.length}
            disabled={saving}
            onMove={(accountId) => void patch({ accountId })}
          />
          <DetailField label="Order">
            <OrderInput value={request.order} ariaLabel="Order" disabled={saving} onCommit={(order) => void patch({ order })} className="w-24" />
            <p className="mt-1 text-xs text-[var(--text-2)]">Within the same priority; 1 first.</p>
          </DetailField>
          <SizingFields estimateDays={request.estimateDays} startedAt={request.startedAt} status={request.status} disabled={saving} onPatch={(p) => void patch(p)} />
          <DetailField label="Account lead">
            <OwnerPicker
              id="rd-lead"
              ariaLabel="Account lead"
              members={withCurrent(request.accountLead)}
              value={request.accountLead?.id ?? ""}
              unassignedLabel="No account lead"
              disabled={saving}
              onChange={(id) => void patch({ accountLeadId: id || null })}
            />
          </DetailField>
          <DetailField label="Technical owner">
            <OwnerPicker
              id="rd-owner"
              ariaLabel="Technical owner"
              members={withCurrent(request.owner)}
              value={request.owner?.id ?? ""}
              unassignedLabel="Unassigned"
              disabled={saving}
              onChange={(id) => void patch({ ownerId: id || null })}
            />
          </DetailField>
          <DetailField label="Final owner team">
            <Select
              aria-label="Final owner team"
              value={request.finalOwner ?? ""}
              disabled={saving}
              onChange={(e) => void patch({ finalOwner: e.target.value || null })}
            >
              <option value="">Not set</option>
              {FINAL_OWNER_ORDER.map((t) => (
                <option key={t} value={t}>
                  {FINAL_OWNER_LABEL[t]}
                </option>
              ))}
            </Select>
          </DetailField>
          <DetailField label="Customer portal">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                aria-label="Visible in customer portal"
                checked={request.portalVisible}
                disabled={saving}
                onChange={(e) => void patch({ portalVisible: e.target.checked })}
                className="h-4 w-4 accent-[var(--brand-600)]"
              />
              Visible to the customer
            </label>
          </DetailField>
          <DetailField label="Tags">
            <TagPicker
              id="rd-tags"
              tags={tags}
              value={request.tags.map((t) => t.id)}
              disabled={saving}
              onChange={(tagIds) => void patch({ tagIds })}
            />
          </DetailField>
          <RequestMilestones
            milestones={request.milestones}
            candidates={accountMilestones}
            disabled={saving}
            onChange={(milestoneIds) => void patch({ milestoneIds })}
          />
          <DetailField label="Contact">{request.contact}</DetailField>
          <DetailField label="Source">
            <div className="flex flex-col gap-2">
              <Select
                aria-label="Source"
                value={request.source}
                disabled={saving}
                onChange={(e) => void patch({ source: e.target.value })}
              >
                {/* A customer-opened request keeps its portal source; staff choose among the rest. */}
                {(request.source === "portal" ? (["portal", ...STAFF_SOURCES] as RequestSource[]) : STAFF_SOURCES).map((s) => (
                  <option key={s} value={s} disabled={s === "portal"}>
                    {SOURCE_LABEL[s]}
                  </option>
                ))}
              </Select>
              <TextInput
                aria-label="Source link"
                value={sourceLink}
                disabled={saving}
                placeholder="Slack permalink, meeting recording, ticket…"
                onChange={(e) => setSourceLink(e.target.value)}
                onBlur={saveSourceLink}
                onKeyDown={(e) => {
                  if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                }}
              />
              {request.sourceLabel !== "—" && /^https?:\/\//.test(request.sourceLabel) && (
                <a href={request.sourceLabel} target="_blank" rel="noreferrer" className="wrap-anywhere text-xs text-brand-600 hover:text-brand-700 focus-visible:focus-ring">
                  Open link
                </a>
              )}
            </div>
          </DetailField>
          <DetailField label="Needed by">
            <div className="flex items-center gap-2">
              <TextInput
                type="date"
                aria-label="Needed by"
                value={request.neededBy ?? ""}
                disabled={saving}
                onChange={(e) => void patch({ neededBy: e.target.value || null })}
              />
              {request.neededBy && (
                <button
                  type="button"
                  onClick={() => void patch({ neededBy: null })}
                  disabled={saving}
                  aria-label="Clear needed-by date"
                  className="flex-none rounded p-1 text-[var(--text-2)] hover:text-[var(--text)] focus-visible:focus-ring"
                >
                  <Icon name="x" />
                </button>
              )}
            </div>
            <p className="mt-1 text-xs text-[var(--text-2)]">When the customer needs it. The commitment date below is our promise.</p>
          </DetailField>
          <DetailField label="Commitment date">
            <div className="flex items-center gap-2">
              <TextInput
                type="date"
                aria-label="Commitment date"
                value={request.nextDate ?? ""}
                disabled={saving}
                onChange={(e) => void patch({ nextDate: e.target.value || null })}
              />
              {request.nextDate && (
                <button
                  type="button"
                  onClick={() => void patch({ nextDate: null })}
                  disabled={saving}
                  aria-label="Clear commitment date"
                  className="flex-none rounded p-1 text-[var(--text-2)] hover:text-[var(--text)] focus-visible:focus-ring"
                >
                  <Icon name="x" />
                </button>
              )}
            </div>
            <p className={`mt-1 text-xs ${NEXT_TONE_CLASS[request.nextTone]}`}>{request.nextLabel}</p>
          </DetailField>
          <DetailField label="SLA">
            <span className={SLA_CLASS[request.sla.state]}>{SLA_LABEL[request.sla.state]}</span>
            {request.sla.reason && <p className="mt-0.5 text-xs text-[var(--text-2)]">{request.sla.reason}</p>}
          </DetailField>
        </DetailAside>
      </div>
    </div>
  );
}

function CommentRow({ comment }: { comment: Comment }) {
  const customer = comment.visibility === "customer";
  return (
    <div className="border-b border-[var(--border)] py-3 last:border-b-0">
      <div className="mb-1 flex items-center gap-2 text-xs">
        <PersonAvatar name={comment.author} avatarUrl={comment.authorAvatarUrl} />
        <span className="font-medium text-gray-800">{comment.author}</span>
        {comment.authorKind === "customer" && (
          // A customer may share a colleague's name; the badge is what tells them apart.
          <span className="rounded-full bg-gray-800 px-1.5 py-0.5 text-[11px] font-medium text-white">Customer</span>
        )}
        <span className="text-[var(--text-3)]">{formatDateTime(comment.at)}</span>
        <span
          className={`rounded-full px-1.5 py-0.5 text-[11px] font-medium ${
            customer ? "bg-brand-50 text-brand-700" : "bg-gray-100 text-[var(--text-2)]"
          }`}
        >
          {customer ? "Customer can see" : "Internal"}
        </span>
      </div>
      <p className="whitespace-pre-line text-sm text-gray-800">{comment.body}</p>
    </div>
  );
}

const KIND_ICON: Record<TimelineEntry["kind"], "plus" | "check" | "clock" | "user"> = {
  created: "plus",
  status: "clock",
  note: "user",
  commitment: "check",
};

function TimelineRow({ entry }: { entry: TimelineEntry }) {
  return (
    <li className="flex gap-3 border-b border-[var(--border)] py-3 last:border-b-0">
      <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-gray-100 text-gray-500">
        <Icon name={KIND_ICON[entry.kind]} />
      </span>
      <div className="min-w-0">
        <p className="text-sm text-gray-800">{entry.text}</p>
        <p className="mt-0.5 flex items-center gap-1.5 text-xs text-[var(--text-3)]">
          {entry.actor && <PersonAvatar name={entry.actor} avatarUrl={entry.actorAvatarUrl} />}
          <span>
            {entry.actor}
            {entry.at && ` · ${formatDateTime(entry.at)}`}
          </span>
        </p>
      </div>
    </li>
  );
}

/** A comment author or activity actor: their Hub photo when the server found
 *  one by name, their initials otherwise (customers always get initials). */
function PersonAvatar({ name, avatarUrl }: { name: string; avatarUrl?: string | null }) {
  const display = name.replace(/ \(customer\)$/, "");
  return <Avatar owner={{ name: display, initials: initialsOf(display), color: "gray", avatarUrl }} size="sm" />;
}
